import type {ThemesResource} from 'common/report/model-resources/themes';
import type {Theme} from 'common/report/queries/app-api/themes';

import {STATIC_THEMES} from './constants';

/**
 * Finds a theme by id on the static list of themes or on project defined ones
 */
function findThemeById(id: string | number, themesResource: ThemesResource = null) {
  if (id === null || id === undefined) {
    return null;
  }

  return (
    STATIC_THEMES.find((staticTheme) => staticTheme.id === id) ??
    (themesResource ? themesResource.getOne(id).value : null)
  );
}

/**
 * Checks if the provided theme is an inline theme
 */
function isTheme(theme: Theme | {id: number | string}): theme is Theme {
  const themeData = (theme as Theme)?.data ?? null;
  return themeData && (`colors` in themeData || `darkColors` in themeData);
}

/** Resolves a theme by:
 * * retrieving it from the list of static themes if ID matches a static theme;
 * * retrieving it from the themes api resource if it's a saved theme (with an ID)
 * * returning the passed in theme if it's an inline theme (without an ID) */
export function resolveTheme({
  theme,
  themesResource,
  defaultThemeId = null,
}: {
  theme?: Theme | {id: number | string};
  themesResource?: ThemesResource;
  defaultThemeId?: number | string;
}): Theme {
  if (isTheme(theme)) {
    return theme;
  } else {
    return (
      findThemeById(theme?.id, themesResource) ?? findThemeById(defaultThemeId, themesResource) ?? STATIC_THEMES[0]
    );
  }
}

export function generateInlineTheme(theme: Theme): Theme {
  return {
    id: undefined,
    name: `Custom Theme`,
    type: theme.type,
    data: {...theme.data},
  };
}
